import React, { useState, useEffect } from "react";
import { X, Save, RefreshCcw } from "lucide-react";
import { adminLifecycleService } from "../../../../services/api/settings";
import { LifecycleVolumeDiscount } from "../../../../store/types/settings";

interface EditLifecycleDiscountModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: LifecycleVolumeDiscount | null; // null means create mode
  onSuccess: () => void;
}

const EditLifecycleDiscountModal: React.FC<EditLifecycleDiscountModalProps> = ({
  isOpen,
  onClose,
  data,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    label: "",
    minAdditionalPhases: 0,
    maxAdditionalPhases: "" as number | string,
    allPhases: false,
    discountPct: 0,
    appliesTo: "ALL",
  });

  useEffect(() => {
    if (data) {
      setFormData({
        label: data.label || "",
        minAdditionalPhases: data.minAdditionalPhases || 0,
        maxAdditionalPhases: data.maxAdditionalPhases !== null && data.maxAdditionalPhases !== undefined ? data.maxAdditionalPhases : "",
        allPhases: !!data.allPhases,
        discountPct: data.discountPct || 0,
        appliesTo: data.appliesTo || "ALL",
      });
    } else {
      setFormData({
        label: "",
        minAdditionalPhases: 0,
        maxAdditionalPhases: "",
        allPhases: false,
        discountPct: 0,
        appliesTo: "ALL",
      });
    }
  }, [data, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        label: formData.label,
        minAdditionalPhases: formData.allPhases ? 0 : Number(formData.minAdditionalPhases),
        maxAdditionalPhases: formData.allPhases || formData.maxAdditionalPhases === "" ? null : Number(formData.maxAdditionalPhases),
        allPhases: formData.allPhases,
        discountPct: Number(formData.discountPct),
        appliesTo: formData.appliesTo,
      };
      if (data) {
        await adminLifecycleService.volumeDiscounts.update(data.id, payload);
      } else {
        await adminLifecycleService.volumeDiscounts.create(payload);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Save discount failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {data ? "Edit Volume Discount" : "Add Volume Discount"}
            </h2>
            <p className="text-sm text-gray-500 font-medium">
              {data ? "Update volume discount rules" : "Create a new volume discount tier"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 flex flex-col">
          <div className="p-6 space-y-6 flex-1">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                  Discount Label
                </label>
                <input
                  type="text"
                  required
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="e.g. 3+ Phases Discount, Enterprise Scale"
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                />
              </div>

              <div className="flex items-end mb-4">
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl w-full border border-transparent hover:border-primary/10 transition-all">
                  <input
                    type="checkbox"
                    checked={formData.allPhases}
                    onChange={(e) => setFormData({ ...formData, allPhases: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary transition-all cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-bold text-gray-700 block">
                      Applies to All Phases
                    </span>
                    <span className="text-xs text-gray-400 font-medium">
                      If checked, discount will apply regardless of phase count ranges.
                    </span>
                  </div>
                </label>
              </div>

              {!formData.allPhases && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                      Min Additional Phases
                    </label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={formData.minAdditionalPhases}
                      onChange={(e) => setFormData({ ...formData, minAdditionalPhases: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                      Max Additional Phases (leave empty for unlimited)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.maxAdditionalPhases}
                      onChange={(e) => setFormData({ ...formData, maxAdditionalPhases: e.target.value === "" ? "" : parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                    Discount Percentage (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    value={formData.discountPct}
                    onChange={(e) => setFormData({ ...formData, discountPct: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                    Applies To
                  </label>
                  <select
                    value={formData.appliesTo}
                    onChange={(e) => setFormData({ ...formData, appliesTo: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  >
                    <option value="ALL">All Fees (Setup & Monthly)</option>
                    <option value="SETUP_FEE">Setup Fee Only</option>
                    <option value="MONTHLY_SUBSCRIPTION">Monthly Subscription Only</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-gray-100 flex items-center justify-between bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-8 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all shadow-md shadow-primary/20 font-bold disabled:opacity-50"
            >
              {loading ? (
                <RefreshCcw size={20} className="animate-spin" />
              ) : (
                <Save size={20} />
              )}
              <span>{loading ? "Saving..." : "Save Discount"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLifecycleDiscountModal;
